import { Filters } from '@genesislcap/foundation-forms';

/**
 * Represents the complete filter state persisted to sessionStorage.
 */
export interface SavedFilterState {
  filterValue: string;
  filterName: string;
  timeModeCriteriaValue: string;
  bidModeCriteriaValue: string;
  watchlistValue: string;
  filtersData: Record<string, unknown> | null;
  bidModeSelectedLabel: string | null;
  timeModeSelectedLabel: string | null;
  watchlistSelectedLabel: string | null;
  bidTimeHoursMin?: number | null;
  bidTimeHoursMax?: number | null;
}

/**
 * Handles sessionStorage persistence for bond-bidding filter state.
 * Encapsulates save/load/clear operations and state validation.
 */
export class BondBiddingFilterCache {
  private static readonly KEY_PREFIX = 'bond-bidding-filter-state';
  private readonly key: string;

  constructor(userName: string) {
    this.key = `${BondBiddingFilterCache.KEY_PREFIX}:${userName}`;
  }

  /**
   * Persists filter state to sessionStorage.
   */
  save(state: SavedFilterState): void {
    try {
      sessionStorage.setItem(this.key, JSON.stringify(state));
    } catch {
      // sessionStorage may be unavailable
    }
  }

  /**
   * Loads and validates filter state from sessionStorage.
   * Returns null if no state exists or validation fails.
   */
  load(): SavedFilterState | null {
    try {
      const raw = sessionStorage.getItem(this.key);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return BondBiddingFilterCache.isValidState(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Removes persisted filter state from sessionStorage.
   */
  clear(): void {
    try {
      sessionStorage.removeItem(this.key);
    } catch {
      // sessionStorage may be unavailable
    }
  }

  /**
   * Type guard to validate saved filter state structure.
   */
  private static isValidState(value: unknown): value is SavedFilterState {
    if (typeof value !== 'object' || value === null) return false;
    const obj = value as Record<string, unknown>;
    return (
      typeof obj.filterValue === 'string' &&
      typeof obj.filterName === 'string' &&
      (obj.bidModeSelectedLabel === null || typeof obj.bidModeSelectedLabel === 'string') &&
      (obj.timeModeSelectedLabel === null || typeof obj.timeModeSelectedLabel === 'string') &&
      (obj.watchlistSelectedLabel === null || typeof obj.watchlistSelectedLabel === 'string') &&
      (obj.bidTimeHoursMin === undefined ||
        obj.bidTimeHoursMin === null ||
        typeof obj.bidTimeHoursMin === 'number') &&
      (obj.bidTimeHoursMax === undefined ||
        obj.bidTimeHoursMax === null ||
        typeof obj.bidTimeHoursMax === 'number')
    );
  }
}

/**
 * Restores the Saved Filters dropdown selection after KV-backed options have loaded.
 * Waits for the filter option to appear, then sets the selection while preserving
 * the already-restored criteria and form data.
 *
 * @returns A cleanup function that can be called to abort the restore if needed.
 */
export async function restoreSavedFilterDropdown(
  filters: Filters,
  filterName: string,
  preservedCriteria: string,
  preservedData: Record<string, unknown> | null,
  onCriteriaOverwritten: (criteria: string) => void,
): Promise<void> {
  if (!filters || !filterName) return;

  const found = await waitForSavedFilterOption(filters, filterName);
  if (!found) return;

  // Setting filterName triggers loadFilterData + searchFilters in foundation-filters.
  // We capture current state and restore it after the async operations complete.
  filters.filterName = filterName;

  // Wait for filterNameChanged side effects to settle
  await filters.loadFilterData(filterName);
  await nextFrames(2);

  // Restore the preserved state that may have been overwritten
  if (preservedData) {
    filters.data = preservedData;
  }
  onCriteriaOverwritten(preservedCriteria);
}

/**
 * Polls for a saved filter option to appear in the filters dropdown.
 * KV storage loads asynchronously, so options may not be immediately available.
 */
async function waitForSavedFilterOption(
  filters: Filters,
  filterName: string,
  maxAttempts = 40,
  intervalMs = 25,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await filters.loadAllSavedFilters();
    if (filters.allSavedFilters?.some((entry) => entry.value === filterName)) {
      return true;
    }
    await sleep(intervalMs);
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function nextFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining--;
      if (remaining <= 0) {
        resolve();
      } else {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  });
}
